import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OrganizationService, type OrganizationData } from '../services/organizationService';
import type { Organization } from '../types';

/**
 * 組織を作成するhook
 */
export const useCreateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OrganizationData) => OrganizationService.createOrganization(data),
    onSuccess: () => {
      // 組織リストのキャッシュを無効化
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
};

/**
 * 組織情報を取得するhook
 */
export const useOrganization = (organizationId: string | null) => {
  return useQuery({
    queryKey: ['organization', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID is required');
      return OrganizationService.getOrganizationById(organizationId);
    },
    enabled: !!organizationId,
  });
};

/**
 * 組織情報を更新するhook
 */
export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<OrganizationData> }) =>
      OrganizationService.updateOrganization(id, data),
    onSuccess: (updatedOrg) => {
      // 個別の組織情報キャッシュを更新
      queryClient.setQueryData(['organization', updatedOrg.id], updatedOrg);
      // 組織リストのキャッシュも無効化
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
};

/**
 * 組織の統計情報を取得するhook
 */
export const useOrganizationStats = (organizationId: string | null) => {
  return useQuery({
    queryKey: ['organization-stats', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID is required');
      return OrganizationService.getOrganizationStats(organizationId);
    },
    enabled: !!organizationId,
  });
};